/**
 * The workspaces a trader starts with.
 *
 * Three, not one, because the argument for workspaces is that a trader does
 * different things at different times of day and wants the screen to follow.
 * Shipping a single default would make the feature look like a settings screen
 * nobody opens.
 *
 * ## They are seeds, not templates
 *
 * A preset is copied into the trader's own workspaces on first run and is
 * theirs from that moment — renaming one, or gutting it, is expected. AEGIS
 * does not re-apply a preset over a workspace somebody has edited, which is
 * the behaviour that makes people stop trusting a layout to stay put.
 *
 * ## No default trading style
 *
 * CLAUDE.md is explicit that the product must not be shaped around how one
 * trader works. These three are named for the ACTIVITY rather than for a
 * style — "During the session", "Reviewing", "Macro" — and none of them
 * assumes a timeframe, a market or a holding period.
 */
import type { PanelKind } from './panels.js';
import { normaliseWorkspace, type Workspace } from './layout.js';

export interface WorkspacePreset {
  readonly id: string;
  readonly name: string;
  readonly summary: string;
  readonly columns: ReadonlyArray<{ readonly panes: readonly PanelKind[]; readonly weight: number }>;
}

export const WORKSPACE_PRESETS: readonly WorkspacePreset[] = [
  {
    id: 'session',
    name: 'During the session',
    summary: 'The chart, what the rules allow, and what is open.',
    columns: [
      { panes: ['chart'], weight: 3 },
      { panes: ['rules', 'positions'], weight: 1 },
    ],
  },
  {
    id: 'review',
    name: 'Reviewing',
    summary: 'Closed trades beside the chart, with your own precedent.',
    columns: [
      { panes: ['journal'], weight: 2 },
      { panes: ['chart', 'precedent'], weight: 3 },
    ],
  },
  {
    id: 'macro',
    name: 'Macro',
    summary: 'Releases and exposure, for the hour around a print.',
    columns: [
      { panes: ['news'], weight: 2 },
      { panes: ['chart'], weight: 3 },
      { panes: ['portfolio', 'positions'], weight: 1 },
    ],
  },
];

/** A preset as a real workspace, ready to store. */
export function fromPreset(preset: WorkspacePreset, id = preset.id): Workspace {
  return normaliseWorkspace({
    id,
    name: preset.name,
    columns: preset.columns.map((c) => ({
      weight: c.weight,
      // Equal shares within a column. A preset that shipped opinionated
      // vertical splits would be encoding a way of reading a screen, which is
      // exactly what "no default trading style" rules out.
      // `id: kind` is what `normaliseWorkspace` would assign to a first
      // instance anyway. Stating it keeps a preset a valid `Workspace` rather
      // than something only that function can finish.
      panes: c.panes.map((kind) => ({ id: kind, kind, weight: 1 })),
    })),
    detached: [],
  });
}

/** The set a brand-new account starts with. */
export function defaultWorkspaces(): readonly Workspace[] {
  return WORKSPACE_PRESETS.map((p) => fromPreset(p));
}
